import React, { useMemo } from 'react';
import Cookies from 'js-cookie';
import styled, { useTheme } from 'styled-components/macro';
import { FaSignOutAlt } from 'react-icons/fa';
import { isAdminSession } from 'lib/utils/auth';
import { Button, Typography } from 'react-covideo-common';
import { Flex } from '../styles/layout';
import { COOKIE_NAME } from 'configs/api/types';
import { logout } from 'lib/api';

const Container = styled.div`
  position: sticky;
  top: 64px;
  z-index: 9;
  padding: 12px 24px;
  cursor: default;
  background-color: #dc3545;
`;

type Props = {
  userData: { firstName?: string; lastName?: string; email?: string };
};

const safeDecode = (v?: string) => {
  if (!v) return '';
  try {
    // decodeURIComponent on UTF-8; avoid deprecated escape()
    return decodeURIComponent(v);
  } catch {
    return v;
  }
};

export const AdminSession = ({ userData }: Props) => {
  const { colors } = useTheme();

  // compute display name once
  const name = useMemo(() => {
    const first = userData.firstName ?? '';
    const last = safeDecode(userData.lastName);
    return `${first} ${last}`.trim();
  }, [userData.firstName, userData.lastName]);

  const handleAdminSignOut = async () => {
    await logout(true);
    Cookies.remove(COOKIE_NAME.ADMIN_JWT);
    window.close();
  };
  // Only render banner for admin-impersonation session
  if (!isAdminSession()) {
    return null;
  }

  return (
    <Container>
      <Flex
        flexDirection='row'
        alignItems='center'
        justifyContent='space-between'
        gap='16px'
      >
        <Typography
          as='p'
          fontWeight={600}
          color={colors.white[100]}
          style={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
          title={`You are currently signed in as ${name} <${userData.email ?? ''}>`}
        >
          You are currently signed in as {name} {'<'}
          {userData.email ?? ''}
          {'>'}
        </Typography>

        {/* Second child: content-sized */}
        <Button
          variant='white'
          style={{ width: 'fit-content' }}
          icon={<FaSignOutAlt />}
          iconPosition='left'
          text={`Sign out of ${name}'s account`}
          onClick={handleAdminSignOut}
        />
      </Flex>
    </Container>
  );
};
